import {
  COMPANY_LIFECYCLE_OPTIONS,
  COMPANY_LIFECYCLE_STAGE_OPTIONS,
} from "../../constants/companyFields";

export const MINING_METHOD_OPTIONS = [
  { value: "open_pit", label: "Surface / Open Pit" },
  { value: "underground", label: "Underground" },
  { value: "both", label: "Both" },
  { value: "stockpile", label: "Stockpile" },
  { value: "other", label: "Other" },
];

export const CLASSIFICATION_OPTIONS = [
  { value: "proven", label: "Proven" },
  { value: "probable", label: "Probable" },
  { value: "measured", label: "Measured" },
  { value: "indicated", label: "Indicated" },
  { value: "inferred", label: "Inferred" },
];

export const createDefaultClassificationRows = (
  classificationOptions = CLASSIFICATION_OPTIONS
) =>
  classificationOptions.map((opt) => ({
    classification_level: opt.value,
    tonnage_mt: "0",
    grade_values: {},
    contained_values: {},
  }));

export const normalizeClassificationTable = (table, commodityIds = []) => {
  const normalizedCommodityIds = commodityIds || [];
  const normalizedCommodityKeys = normalizedCommodityIds.map((id) => `${id}`);
  const rows = (table.rows || []).map((row) => {
    const grade_values = { ...(row.grade_values || {}) };
    const contained_values = { ...(row.contained_values || {}) };
    normalizedCommodityIds.forEach((id) => {
      const key = `${id}`;
      if (grade_values[key] === undefined) grade_values[key] = "0";
      if (contained_values[key] === undefined) contained_values[key] = "0";
    });
    Object.keys(grade_values).forEach((id) => {
      if (!normalizedCommodityKeys.includes(id)) delete grade_values[id];
    });
    Object.keys(contained_values).forEach((id) => {
      if (!normalizedCommodityKeys.includes(id)) delete contained_values[id];
    });
    return { ...row, grade_values, contained_values };
  });
  return {
    ...table,
    commodity_ids: normalizedCommodityIds,
    rows,
  };
};

export const createDefaultClassificationTable = (
  classificationOptions = CLASSIFICATION_OPTIONS
) =>
  normalizeClassificationTable({
    commodity_ids: [],
    rows: createDefaultClassificationRows(classificationOptions),
  });

export const buildClassificationTableFromValues = (
  values,
  classificationOptions = CLASSIFICATION_OPTIONS
) => {
  if (!Array.isArray(values) || values.length === 0) {
    return createDefaultClassificationTable(classificationOptions);
  }
  const commodityIds = [];
  const commodityKeys = new Set();
  const rowsByClassification = classificationOptions.reduce((acc, opt) => {
    acc[opt.value] = {
      classification_level: opt.value,
      tonnage_mt: "0",
      grade_values: {},
      contained_values: {},
    };
    return acc;
  }, {});
  values.forEach((val) => {
    const commodityId = val.commodity?.id || val.commodity;
    if (!commodityId) return;
    const commodityKey = `${commodityId}`;
    if (!commodityKeys.has(commodityKey)) {
      commodityKeys.add(commodityKey);
      commodityIds.push(commodityId);
    }
    const row = rowsByClassification[val.classification_level];
    if (!row) return;
    if (row.tonnage_mt === "0" && val.tonnage_mt !== null && val.tonnage_mt !== undefined) {
      row.tonnage_mt = val.tonnage_mt;
    }
    row.grade_values[commodityKey] = val.grade_value ?? "0";
    row.contained_values[commodityKey] = val.contained_value ?? "0";
  });
  const rows = classificationOptions.map((opt) => rowsByClassification[opt.value]);
  return normalizeClassificationTable({ commodity_ids: commodityIds, rows }, commodityIds);
};

export const getMiningMethodLabel = (value) =>
  MINING_METHOD_OPTIONS.find((opt) => opt.value === value)?.label || value || "N/A";

export const getLifecycleLabel = (value) =>
  COMPANY_LIFECYCLE_OPTIONS.find((opt) => opt.value === value)?.label || value || "N/A";

export const getLifecycleStageLabel = (value) =>
  COMPANY_LIFECYCLE_STAGE_OPTIONS.find((opt) => opt.value === value)?.label ||
  value ||
  "N/A";

export const getLatestReportValuesByReserve = (reserveId, resourceReportsByReserve) => {
  const reports = resourceReportsByReserve[reserveId] || [];
  const sorted = [...reports].sort((a, b) => {
    const aDate = a.period?.as_of_date || "";
    const bDate = b.period?.as_of_date || "";
    return aDate < bDate ? 1 : -1;
  });
  return sorted[0]?.values || [];
};

export const getAssetCommodityRollup = ({
  assetId,
  resourceReservesByAsset,
  resourceReportsByReserve,
  commodities,
  classificationOptions = CLASSIFICATION_OPTIONS,
}) => {
  const reserves = resourceReservesByAsset[assetId] || [];
  const rollup = {};
  reserves.forEach((reserve) => {
    const values = getLatestReportValuesByReserve(reserve.id, resourceReportsByReserve);
    values.forEach((val) => {
      const commodityId = val.commodity?.id || val.commodity;
      if (!commodityId) return;
      if (!rollup[commodityId]) {
        rollup[commodityId] = {
          commodity: val.commodity || commodities.find((item) => item.id === commodityId),
          totals: classificationOptions.reduce((acc, opt) => {
            acc[opt.value] = 0;
            return acc;
          }, {}),
        };
      }
      if (rollup[commodityId].totals[val.classification_level] !== undefined) {
        rollup[commodityId].totals[val.classification_level] += Number(
          val.contained_value || 0
        );
      }
    });
  });
  return Object.values(rollup);
};

export const getCompanyCommodityRollup = ({
  assets,
  resourceReservesByAsset,
  resourceReportsByReserve,
  commodities,
  classificationOptions = CLASSIFICATION_OPTIONS,
}) => {
  const rollup = {};
  assets.forEach((asset) => {
    const reserves = resourceReservesByAsset[asset.id] || [];
    reserves.forEach((reserve) => {
      const values = getLatestReportValuesByReserve(reserve.id, resourceReportsByReserve);
      values.forEach((val) => {
        const commodityId = val.commodity?.id || val.commodity;
        if (!commodityId) return;
        if (!rollup[commodityId]) {
          rollup[commodityId] = {
            commodity: val.commodity || commodities.find((item) => item.id === commodityId),
            totals: classificationOptions.reduce((acc, opt) => {
              acc[opt.value] = 0;
              return acc;
            }, {}),
          };
        }
        if (rollup[commodityId].totals[val.classification_level] !== undefined) {
          rollup[commodityId].totals[val.classification_level] += Number(
            val.contained_value || 0
          );
        }
      });
    });
  });
  return Object.values(rollup);
};
